import React, { useState, useCallback } from "react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { useDateRangeSelector, setLoading } from "../../../redux/actions/app";
import { useDispatch } from "react-redux";
import Axios from "axios";
import CONFIG from "../../../model/TSConfig";
import { tt } from "../../../utils/i18n/i18n";
import moment from "moment-timezone";
import { saveAs } from "file-saver";
import { PaginatedTable } from "./PaginatedTable";

const PAGE_SIZE = 20;

interface FeedbackEntry {
  ts: number;
  feedback: string;
}

export const FeedbacksTable = () => {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const dateRange = useDateRangeSelector();
  const [totalCount, setTotalCount] = useState(0);
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackEntry[]>([]);

  const fetchCount = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/feedbacks/count`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        }
      });

      if (response.data?.payload) {
        setTotalCount(response.data.payload);
      }
    } catch (error) {
      console.error("Error fetching feedbacks count:", error);
      setTotalCount(0);
    }
  }, [accountToDisplay?.trackingID, dateRange]);

  const fetchFeedbacks = useCallback(
    async (pageIndex: number) => {
      if (!accountToDisplay?.trackingID || !dateRange) {
        return;
      }

      dispatch(setLoading(true));

      try {
        const response = await Axios({
          method: "get",
          url: `${CONFIG.jetpackBackendUrl}/custdash/feedbacks`,
          params: {
            owner: accountToDisplay?.trackingID,
            sts: moment(dateRange.start).unix(),
            ets: moment(dateRange.end).unix(),
            pageIndex,
            pageSize: PAGE_SIZE
          }
        });

        if (response.data?.payload) {
          setFeedbackEntries(response.data.payload);
        }
      } catch (error) {
        console.error("Error fetching feedbacks:", error);
        setFeedbackEntries([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [accountToDisplay?.trackingID, dateRange, dispatch]
  );

  const handleDownload = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/feedbacks/csv`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        },
        responseType: "blob"
      });

      const startDate = moment(dateRange.start).format("YYYY-MM-DD");
      const endDate = moment(dateRange.end).format("YYYY-MM-DD");
      const fileName = `feedbacks_${accountToDisplay?.placeName}_${startDate}_${endDate}.csv`;

      saveAs(response.data, fileName);
    } catch (error) {
      console.error("Error downloading feedbacks CSV:", error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [accountToDisplay, dateRange, dispatch]);

  const renderTableHeaders = useCallback(() => {
    return (
      <tr>
        <th>{tt("date_and_hour")}</th>
        <th>{tt("feedback")}</th>
      </tr>
    );
  }, []);

  const renderTableRow = useCallback((entry: FeedbackEntry, index: number) => {
    return (
      <tr key={`feedback-${index}`}>
        <td>{moment.unix(entry.ts).format(tt("date_format_full_date"))}</td>
        <td>{entry.feedback}</td>
      </tr>
    );
  }, []);

  if (!accountToDisplay?.trackingID) {
    return null;
  }

  return (
    <div className="feedbacks-container">
      <PaginatedTable<FeedbackEntry>
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        data={feedbackEntries}
        onFetchCount={fetchCount}
        onFetchData={fetchFeedbacks}
        onDownload={handleDownload}
        renderHeaders={renderTableHeaders}
        renderRow={renderTableRow}
        keyPrefix="feedbacks"
        showDownloadButton={true}
        trackingID={accountToDisplay.trackingID}
      />
    </div>
  );
};
